import React from "react";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import { BriefScanReport } from "@sscs/types";
import { CheckLabel, LinearProgress, Tooltip } from "@sscs/ui";
import { transformScanDuration } from "@sscs/utils";
import { ProcessingStatus } from "Types";
import { TimeStatus } from "../index";
import styles from "./fileStatus.module.scss";

type StatusFailedProps = { description: string };

const StatusFailed = ({
  description,
}: StatusFailedProps): React.JSX.Element => {
  return (
    <Tooltip trigger="term" title={`System error: ${description}`}>
      <span className={styles["file-status"]}>ERROR</span>
    </Tooltip>
  );
};

type StatusInProgressProps = { status: ProcessingStatus };

const StatusInProgress = ({
  status,
}: StatusInProgressProps): React.JSX.Element => {
  return (
    <Tooltip
      trigger="graphic"
      title={
        <>
          <div>{status.status_details}</div>
          <div>
            Started: <TimeStatus timestamp={status.timestamp} />
          </div>
        </>
      }
    >
      <div className={styles["in-progress"]}>
        <div className={styles["progress-bar-container"]}>
          <LinearProgress
            color="secondary"
            progress={status.progress ?? undefined}
            className={styles["linear-progress-bar"]}
          />
        </div>
        <div className={styles["progress-label"]}>
          {status.code.toLowerCase()}
        </div>
      </div>
    </Tooltip>
  );
};

type StatusDoneProps = {
  scanReport: BriefScanReport | undefined;
  scanDuration: string | undefined;
};

const StatusDone = ({
  scanReport,
  scanDuration,
}: StatusDoneProps): React.JSX.Element => {
  const { status, scanLevel, isRepro, isDiff } = convertFinalCheckLabel(
    scanReport?.checks,
  );

  return (
    <Tooltip
      trigger="graphic"
      title={
        scanDuration
          ? `Scan duration: ${transformScanDuration(scanDuration)}`
          : undefined
      }
    >
      <CheckLabel
        status={status}
        scanLevel={scanLevel}
        isLevelCustomized={scanReport?.inhibitors.customized}
        isReproducibilityCheck={isRepro}
        isDiffCheck={isDiff}
      />
    </Tooltip>
  );
};

type FileStatusProps = {
  status: ProcessingStatus | undefined;
  scanReport: BriefScanReport | undefined;
  scanDuration: string | undefined;
};

const FileStatus = ({
  status,
  scanReport,
  scanDuration,
}: FileStatusProps): React.JSX.Element => {
  switch (status?.code) {
    case "ERROR":
      return <StatusFailed description={status.status_details} />;
    case "SECURING":
    case "IMPORTING":
    case "PROCESSING":
      return <StatusInProgress status={status} />;
    case "DONE":
      return <StatusDone scanReport={scanReport} scanDuration={scanDuration} />;
    default:
      /* Catch all status in case of unsupported code so the column doesn't look empty
       * which is often associated with "broken" */
      return <span className={styles["file-status"]}>Unknown</span>;
  }
};

export default FileStatus;
